package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * How far one locale is from shippable, in strings.
 *
 * **Counts, never percentages.** A workspace-wide percent averages a finished German with a 3%
 * Japanese and means nothing; "40 untranslated" is a number somebody can act on and can watch go
 * down.
 *
 * The five counters answer five different questions and deliberately overlap: a machine string that
 * is awaiting review is honestly both. Only [untranslated] is disjoint from the rest — a string
 * nobody has typed cannot also be awaiting review, carry a QA error, or be unreviewed machine
 * output.
 */
@Serializable
data class LocaleReadinessDto(
    val locale: String,
    /** Every key in the project. The denominator, so a count reads as a fraction of something. */
    val total: Int,
    /** No value at all. The one counter that overlaps with nothing else here. */
    val untranslated: Int,
    /** Typed, not signed off: `TRANSLATED`. */
    val awaitingReview: Int,
    /**
     * Translated strings carrying at least one blocking QA error, at this project's severities.
     *
     * **Strings, not issues** — fixing one string can clear three checks, so an issue count
     * overstates the work. And **translated** strings only: `EMPTY` is an ERROR check that fires on
     * every untranslated string, so counting those here would report the same 400 strings twice and
     * make the sentence below a lie.
     */
    val qaErrors: Int,
    /**
     * Strings with an unresolved question against them.
     *
     * An unresolved comment, which is the honest proxy: nothing in the schema marks a comment as a
     * question, and a translator who asked something and got no answer is blocked whether or not
     * anybody labelled it.
     */
    val blocked: Int,
    /** Machine output nobody has approved. Shipping it unreviewed is the thing this counter exists to stop. */
    val machineUnreviewed: Int,
    /** Signed off. */
    val approved: Int,
) {
    /** Nothing in the way. The gate, and the only place the five counters are read as one answer. */
    val shippable: Boolean
        get() = untranslated == 0 && awaitingReview == 0 && qaErrors == 0 &&
            blocked == 0 && machineUnreviewed == 0

    /**
     * How far from shippable, for ordering. Worst first, never alphabetically.
     *
     * A plain sum, because weighting one counter above another would be an opinion about which
     * problem is worse that nobody asked for — and the counters are on screen beside it anyway.
     */
    val distance: Int
        get() = untranslated + awaitingReview + qaErrors + blocked + machineUnreviewed
}

/**
 * Can we ship, and if not, what is in the way.
 *
 * Computed live from `translations` and `qa` on every call rather than from a rollup table. That is
 * deliberate and it is why the rollup tables were cut: this is **also the CI gate**, and a gate that
 * reads a table refreshed by a nightly job blocks a release on yesterday's data — or, worse, passes
 * one on it.
 */
@Serializable
data class ReadinessDto(
    val projectSlug: String,
    val projectName: String,
    val sourceLocale: String,
    /** Sorted by [LocaleReadinessDto.distance], worst first. */
    val locales: List<LocaleReadinessDto> = emptyList(),
    /**
     * The ship-gate sentence: "de is release-ready. fr has 12 QA errors and 40 unreviewed machine
     * strings."
     *
     * Built server-side so the band, the CLI and the MCP tool cannot each summarise the same numbers
     * differently.
     */
    val summary: String,
) {
    val shippable: Boolean get() = locales.isNotEmpty() && locales.all { it.shippable }
}

/**
 * What a CI run refuses to let through.
 *
 * `lingvo qa --fail-on error` is the common case; `--fail-on none` exists so a pipeline can print
 * the numbers without gaining a veto, which is how most teams start.
 */
enum class GateLevel { NONE, ERROR, WARNING }

/**
 * One QA check, as this project has it.
 *
 * Every check is listed, whether or not the project changed it, so a settings page can render the
 * whole set without shipping its own copy of the defaults — two lists of eight checks is one list
 * that eventually disagrees with the engine.
 */
@Serializable
data class QaRuleDto(
    val check: QaCheck,
    val enabled: Boolean,
    /** The severity in force: the project's, or the one the check itself reports. */
    val severity: QaSeverity,
    /** True when the project has overridden the default. Drives "reset" in the UI. */
    val customised: Boolean,
    /** Whether this check can block approval as configured. */
    val blocksApproval: Boolean,
    /**
     * Languages that disagree with the row above, empty for most checks.
     *
     * Sent with the project-wide answer rather than fetched separately, because a page rendering
     * "ERROR everywhere except Japanese" needs both to say either, and two requests is how the two
     * halves end up disagreeing on screen.
     */
    val localeOverrides: List<QaLocaleRuleDto> = emptyList(),
)

/**
 * One language's disagreement with the project about one check.
 *
 * Present only where a language has an opinion of its own — the same rule the project-wide rows
 * follow, and the reason a settings page can tell "unset" from "set to the same thing".
 */
@Serializable
data class QaLocaleRuleDto(
    val locale: String,
    val enabled: Boolean,
    /** The severity in force for this language: null when the language switched the check off. */
    val severity: QaSeverity?,
)

@Serializable
data class QaRulesResponse(val rules: List<QaRuleDto> = emptyList())

/** Turn a check off, or change what it costs. Owners and admins only. */
@Serializable
data class UpdateQaRuleRequest(
    val enabled: Boolean = true,
    /** Null means "whatever the check itself says", which is the common case. */
    val severity: QaSeverity? = null,
    /**
     * The one language this applies to, or null for the project.
     *
     * A language's row wins outright over the project's rather than merging with it, so writing one
     * here is a complete answer for that language and not a patch on top of another.
     */
    val locale: String? = null,
)

/**
 * One string exempted from one check.
 *
 * The per-string escape hatch, and the reason the project-wide severity switch can stay strict. A
 * team whose product is called "OK" should not have to turn `SAME_AS_SOURCE` down to a warning
 * everywhere — losing it on the strings where it was right — to stop it firing on one.
 */
@Serializable
data class QaIgnoreDto(
    val id: String,
    val keyId: String,
    val keyName: String,
    /** Null means every language: a source-side quirk is not a German problem. */
    val locale: String? = null,
    val check: QaCheck,
    /**
     * Why. Required by the route rather than by the column.
     *
     * Six months on, the difference between an exemption and an oversight is this sentence, and a
     * database constraint would be satisfied by a space.
     */
    val reason: String = "",
    val createdBy: String? = null,
    val createdAt: String,
)

@Serializable
data class QaIgnoresDto(val ignores: List<QaIgnoreDto> = emptyList())

/** Exempt one string. `locale = null` exempts it in every language. */
@Serializable
data class CreateQaIgnoreRequest(
    val keyId: String,
    val locale: String? = null,
    val check: QaCheck,
    val reason: String,
)

/** One string in one language, as [CreateQaIgnoresRequest] addresses it. */
@Serializable
data class QaIgnoreTarget(
    val keyId: String,
    /** Null means every language, exactly as in [CreateQaIgnoreRequest]. */
    val locale: String? = null,
)

/**
 * Exempt many strings from **one** check, for **one** reason.
 *
 * The shape is the whole argument. Dismissing findings one at a time is the most-cited complaint
 * about QA in this market — "several seconds per request" — and the reason it is slow is never that
 * the strings are unrelated. It is that one check is wrong for this product: a team whose product
 * is called "OK" trips `SAME_AS_SOURCE` on every language it ships.
 *
 * So [check] and [reason] are single, not per-target. Requiring a distinct sentence per string is
 * what makes bulk exemption impossible, and a bulk action that let each row carry its own check
 * would be a worse spelling of calling the single-string route in a loop.
 */
@Serializable
data class CreateQaIgnoresRequest(
    val targets: List<QaIgnoreTarget>,
    val check: QaCheck,
    val reason: String,
)

/**
 * The most strings one [CreateQaIgnoresRequest] may carry.
 *
 * Shared so the page and the route cannot disagree. The server refuses above it rather than
 * truncating — silently exempting the first thousand of three thousand would leave a reader
 * believing a check was settled while it still blocks two thirds of a release — and the page needs
 * the same number to say so before the request is sent.
 */
const val MAX_QA_IGNORES_PER_REQUEST = 1000

/** How many exemptions a bulk request actually wrote, after duplicates collapsed. */
@Serializable
data class QaIgnoresCreatedDto(val created: Int)

/**
 * The whole QA picture for one project, counted over everything rather than over one page of it.
 *
 * `GET /qa` caps its list at a thousand issues, which is right for a panel and wrong for a heading:
 * a group header reading "12 placeholder mismatches" when the list was truncated at a thousand is a
 * number derived from the page rather than from the project, and it is wrong in the direction that
 * makes somebody think they are nearly done.
 *
 * [issues] is the same capped list the bare route returns, so a caller that wants both needs one
 * request; [countsByCheck], [countsBySeverity] and [total] are counted **before** the cap, over the
 * filtered set, and are the numbers a heading is allowed to state.
 */
@Serializable
data class QaSummaryDto(
    val issues: List<QaIssueDto> = emptyList(),
    /** Keyed by [QaCheck.name], so a client can render a group heading without re-counting. */
    val countsByCheck: Map<String, Int> = emptyMap(),
    /** Keyed by [QaSeverity.name]. */
    val countsBySeverity: Map<String, Int> = emptyMap(),
    /** Every issue the filter matched, whether or not it survived the cap. */
    val total: Int = 0,
) {
    /** True when [issues] is a page of [total] rather than all of it — the sentence the QA page owes. */
    val truncated: Boolean get() = issues.size < total
}
